import { FirewallUser, FirewallEvent, FirewallPackage, FirewallStatusResponse, FirewallComponentState, AlpineMagicProperties, FirewallComponentType } from './types';

export function firewallComponent(): FirewallComponentType {
  return {
    isLoading: true,
    isLoadingUsers: true,
    isLoadingMetrics: true,
    isLoadingEvents: true,
    status: 'Active',
    summaryData: {
      status: 'Loading...',
      users_protected: 0,
      total_users: 0,
      last_updated: 'never',
      unprotected_users: [],
      blocked_packages: 0,
      analyzed_packages: 0
    },
    users: [] as FirewallUser[],
    updates: [] as FirewallEvent[],
    lastHeartbeat: null as string | null,
    eventsPage: 1,
    eventsLimit: 50,
    eventTypeFilters: {
      success: true,
      error: true,
      info: true,
      warning: true,
      blocked: true
    },
    activeFilters: [],
    packageMetrics: {
      packages_installed: 0,
      unique_packages: 0,
      vulnerable_packages: 0
    },
    processedEventIds: new Set<string>(),
    packagesPerPage: 5,
    currentPage: 1,
    isLoadingMore: false,
    hasMorePackages: false,
    hasMoreEvents: true,
    searchDebounceTimer: null,
    searchCache: new Map<string, Array<any>>(),
    isProtectionEnabled: false,
    isFullyConfigured: false,
    lastUpdated: '',
    hasData: false,
    summary: null,
    configurationWarning: null,
    hasPendingRequests: false,
    timeUpdateInterval: null,
    expandedEvents: new Set<number>(),
    currentSearchTerm: '',
    requestTimeoutId: null, // Watchdog timer for pending requests
    boundRefreshHandler: null, // Initialize the property
    inactivityTimeoutId: null as number | null,
    inactivityEvents: [
      'mousemove',
      'keydown',
      'scroll',
      'click',
      'touchstart'
    ],

    $nextTick: (function(callback: () => void) {
      callback();
    }) as AlpineMagicProperties['$nextTick'],
    $data: {} as FirewallComponentState,

    formatDate(dateInput: string | number | Date): string {
      if (!dateInput) return '';

      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getRelativeTime(timestamp: string | number | Date): string {
      if (!timestamp || timestamp === 'Never') return 'Never';

      // Get current time in UTC milliseconds
      const nowUtcMs = Date.now();
      // Parse the timestamp (assume it's UTC or ISO format with timezone)
      const date = new Date(timestamp);

      // Check if the date is valid
      if (isNaN(date.getTime())) return 'Never';

      // Get the event time in UTC milliseconds
      const dateUtcMs = date.getTime();

      // Calculate the difference in UTC milliseconds
      const diffMs = nowUtcMs - dateUtcMs;

      // Ensure difference isn't negative (handles minor clock skew)
      const positiveDiffMs = Math.max(0, diffMs);

      const diffSec = Math.floor(positiveDiffMs / 1000);

      if (diffSec < 60) return `${diffSec} second${diffSec === 1 ? '' : 's'} ago`;

      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;

      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

      const diffWeeks = Math.floor(diffDays / 7);

      // Use Date objects for accurate month/year calculation
      const nowDate = new Date(nowUtcMs);
      const pastDate = new Date(dateUtcMs);

      const yearDiff = nowDate.getUTCFullYear() - pastDate.getUTCFullYear();
      const monthDiff = nowDate.getUTCMonth() - pastDate.getUTCMonth();
      const dayDiff = nowDate.getUTCDate() - pastDate.getUTCDate();

      // Calculate total months difference accurately
      let totalMonthsDiff = yearDiff * 12 + monthDiff;
      // Adjust if the current day is earlier than the past day in the month
      if (dayDiff < 0) {
        totalMonthsDiff -= 1;
      }

      // Adjust if difference is less than a month but spans across month end
      if (diffDays < 31 && totalMonthsDiff === 1 && diffWeeks < 4) {
          // If less than 4 weeks treat as weeks ago
          if (diffWeeks >= 1) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
      } else if (totalMonthsDiff >= 1 && totalMonthsDiff < 12) {
          return `${totalMonthsDiff} month${totalMonthsDiff === 1 ? '' : 's'} ago`;
      } else if (yearDiff >= 1) {
          return `${yearDiff} year${yearDiff === 1 ? '' : 's'} ago`;
      } else {
          // Fallback for cases slightly less than a month but not caught by weeks
          // This should cover the transition between weeks and months more gracefully
          if (diffWeeks >= 1) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
      }

      // Default fallback (should ideally not be reached with the logic above)
      return 'Just now'; // Or adjust as needed
    },

    init() {
      this.isLoading = true;
      this.isLoadingUsers = true;
      this.isLoadingMetrics = true;
      this.isLoadingEvents = true;
      this.status = 'loading';
      this.summaryData = {
        status: 'Loading...',
        users_protected: 0,
        total_users: 0,
        last_updated: 'never',
        unprotected_users: [],
        blocked_packages: 0,
        analyzed_packages: 0
      };
      this.users = [];
      this.updates = [];
      this.lastHeartbeat = null;
      this.eventsPage = 1;
      this.eventsLimit = 20;
      this.eventTypeFilters = {
        success: true,
        error: true,
        info: true,
        warning: true,
        blocked: true
      };
      this.activeFilters = [];
      this.packageMetrics = {
        packages_installed: 0,
        unique_packages: 0,
        vulnerable_packages: 0
      };
      this.processedEventIds = new Set<string>();
      this.packagesPerPage = 5;
      this.currentPage = 1;
      this.isLoadingMore = false;
      this.hasMorePackages = false;
      this.hasMoreEvents = true;
      this.searchDebounceTimer = null;
      this.searchCache = new Map<string, Array<any>>();
      this.isProtectionEnabled = false;
      this.isFullyConfigured = false;
      this.lastUpdated = '';
      this.hasData = false;
      this.summary = null;
      this.configurationWarning = null;
      this.hasPendingRequests = false;
      this.timeUpdateInterval = null;
      this.expandedEvents = new Set<number>();
      this.currentSearchTerm = '';

      // Mark Alpine.js as initialized immediately
      document.body.classList.add('alpine-initialized');

      // Ensure skeleton loaders are visible during initial loading - do this immediately
      this.ensureSkeletonsVisible();

      // Make sure skeletons are visible when the page fully loads
      window.addEventListener('load', () => {
        // Ensure skeletons are visible again after full page load
        this.ensureSkeletonsVisible();
      });

      // Initialize the search clear button functionality
      const searchInput = document.querySelector<HTMLInputElement>('input[placeholder*="Search"]');
      if (searchInput) {
        // Add clear button based on initial search value
        if (searchInput.value.trim()) {
          this.addSearchClearButton(searchInput);
        }

        // Add input event listener to update clear button visibility
        searchInput.addEventListener('input', () => {
          const searchTerm = searchInput.value.trim();
          if (searchTerm) {
            this.addSearchClearButton(searchInput);
          } else {
            this.removeSearchClearButton(searchInput);
            this.currentSearchTerm = '';
          }
        });
      }

      // Start the time update interval
      this.startTimeUpdateInterval();

      // Setup card height matching
      this.setupCardHeightMatching();

      // Fetch data with proper error handling
      Promise.all([
        this.fetchFirewallData()
          .then(() => {
            this.isLoadingUsers = false;
            // Check if we can show content instead of skeletons now
            this.checkAndShowContent();
          })
          .catch((error: Error) => {
            console.error('Error fetching firewall data:', error);
            this.isLoadingUsers = false;
          }),
        this.fetchPackageMetrics()
          .catch(error => {
            console.error('Error fetching package metrics:', error);
            this.isLoadingMetrics = false;
          }),
        this.fetchTelemetryEvents(true)
          .then(() => {
            this.applyEventTypeFilters();
            this.isLoadingEvents = false;
            this.checkAndShowContent();
          })
          .catch((error: any) => {
            console.error('Error fetching telemetry events:', error);
            this.isLoadingEvents = false;
          })
      ]).catch(error => {
        console.error('Error during initialization:', error);
      });

      // Set up periodic refresh of package metrics
      setInterval(() => {
        this.fetchPackageMetrics();
      }, 5 * 60 * 1000);

      // Handle legacy event for backwards compatibility
      window.addEventListener('firewall:update', ((event: Event) => {
        const customEvent = event as CustomEvent;
        this.handleFirewallUpdate(customEvent.detail);
      }) as EventListener);

      window.addEventListener('firewall:heartbeat', ((event: Event) => {
        const customEvent = event as CustomEvent;
        this.lastHeartbeat = customEvent.detail.timestamp;
        this.summaryData.last_updated = customEvent.detail.timestamp;
      }) as EventListener);

      // Handle legacy events for backwards compatibility
      window.addEventListener('firewall:configured', ((event: Event) => {
        const customEvent = event as CustomEvent;
        this.handleFirewallUpdate(customEvent.detail);
        this.status = 'Active';
      }) as EventListener);

      window.addEventListener('firewall:disabled', ((event: Event) => {
        const customEvent = event as CustomEvent;
        this.handleFirewallUpdate(customEvent.detail);
        this.status = 'Disabled';
      }) as EventListener);

      // Add polling mechanism to fetch fresh data every 10 seconds
      setInterval(() => {
        // Only create refresh event if there are no pending requests
        if (!this.hasPendingRequests) {
          // Create and dispatch a firewall:refresh-events event
          const refreshEvent = new CustomEvent('firewall:refresh-events', {
            detail: {
              eventId: `poll-${Date.now()}`,
              eventType: 'polling_refresh'
            }
          });
          window.dispatchEvent(refreshEvent);
        }
      }, 5000); // Changed to 5 seconds

      // Bind the handler once and store it
      this.boundRefreshHandler = this.handleRefreshEvent.bind(this);

      // Add new event listeners for API refresh approach using the bound handler
      window.addEventListener('firewall:refresh-events', this.boundRefreshHandler);

      this.$nextTick(() => {
        this.setupInfiniteScroll();
      });

      // Initialize inactivity timer
      this.inactivityTimeoutId = null;
      // Bind the reset function once to ensure correct 'this' context and allow removal
      const boundResetTimer = this._resetInactivityTimer.bind(this);
      this.inactivityEvents.forEach(event => {
        window.addEventListener(event, boundResetTimer);
      });
      this._resetInactivityTimer(); // Start the timer initially
    },

    startTimeUpdateInterval() {
      // Clear any existing interval to avoid multiple instances
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval);
      }

      // Set up a new interval to update times every 30 seconds
      this.timeUpdateInterval = window.setInterval(() => {
        // Only update if there are events to refresh
        if (this.updates.length > 0) {
          // Create a new array reference to force Alpine.js to re-render
          this.updates = this.updates.map(update => ({
            ...update,
            // Keep the same data but force a re-render
            timestamp: update.timestamp
          }));
        }
      }, 30000); // Update every 30 seconds
    },

    async fetchPackageMetrics() {
      try {
        const response = await fetch('api/package-metrics/');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data) {
          this.packageMetrics = {
            packages_installed: data.packages_installed || 0,
            unique_packages: data.unique_packages || 0,
            vulnerable_packages: data.vulnerable_packages || 0
          };

          // Explicitly hide skeletons and show content ONLY for metrics and policy
          const metricsSkeleton = document.getElementById('metrics-skeleton');
          const metricsContent = document.getElementById('metrics-content');
          const policySkeleton = document.getElementById('policy-skeleton');
          const policyContent = document.getElementById('policy-content');

          const hideElement = (el: Element | null) => {
            if (el) {
              // Immediately and forcefully hide the skeleton
              (el as HTMLElement).setAttribute('style',
                'display: none !important; opacity: 0 !important; visibility: hidden !important;'
              );
            }
          };

          const showElement = (el: Element | null) => {
            if (el) {
              // Remove any !important display styles and set to block for transition
              (el as HTMLElement).setAttribute('style', 'display: block; opacity: 0; visibility: hidden;');
              // Force a reflow before starting the transition
              void (el as HTMLElement).offsetHeight;
              // Start fade-in
              (el as HTMLElement).style.opacity = '1';
              (el as HTMLElement).style.visibility = 'visible';
            }
          };

          hideElement(metricsSkeleton);
          hideElement(policySkeleton);
          showElement(metricsContent);
          showElement(policyContent);

          // DO NOT add metrics-loaded class to body here
        }
      } catch (error) {
        console.error('Error fetching package metrics:', error);
        // Set default values on error
        this.packageMetrics = {
          packages_installed: 0,
          unique_packages: 0,
          vulnerable_packages: 0
        };
      } finally {
        this.isLoadingMetrics = false;
      }
    },

    setupInfiniteScroll() {
      const eventsContainer = document.querySelector('.firewall-events-scroll');
      if (!eventsContainer) return;

      const loadMoreButton = document.querySelector('[data-infinite-scroll="disabled"]');
      if (loadMoreButton) {
        loadMoreButton.removeAttribute('data-infinite-scroll');
        loadMoreButton.setAttribute('hidden', 'true');
      }

      const scrollLoader = document.getElementById('infinite-scroll-loader');

      let isLoading = false;
      let scrollTimeout: number | null = null;
      let lastRequestTime = 0;
      const MIN_REQUEST_INTERVAL = 1000; // Minimum 1 second between requests

      eventsContainer.addEventListener('scroll', () => {
        if (scrollTimeout) {
          window.clearTimeout(scrollTimeout);
        }

        scrollTimeout = window.setTimeout(() => {
          if (isLoading || this.updates.length === 0 || !this.hasMoreEvents) {
            return;
          }

          const { scrollTop, scrollHeight, clientHeight } = eventsContainer;
          // Calculate distance from bottom (percentage scrolled)
          const scrolledPercentage = (scrollTop + clientHeight) / scrollHeight;

          // Always load more when very close to the bottom (99%)
          const atBottom = scrolledPercentage > 0.99;

          // Standard threshold (80% of the way through)
          const pastStandardThreshold = scrolledPercentage > 0.8;

          // Load more if we're at the bottom or past the threshold
          if (atBottom || pastStandardThreshold) {
            const now = Date.now();
            if (now - lastRequestTime < MIN_REQUEST_INTERVAL) {
              return; // Skip if we're making requests too quickly
            }

            isLoading = true;
            lastRequestTime = now;

            if (scrollLoader) {
              scrollLoader.classList.remove('hidden');
            }

            this.loadMoreEvents(true).finally(() => {
              isLoading = false;

              if (scrollLoader) {
                scrollLoader.classList.add('hidden');
              }

              // If we're at the bottom and there might be more events, try loading again after a delay
              if (atBottom && this.hasMoreEvents) {
                setTimeout(() => {
                  const { scrollTop: newScrollTop, scrollHeight: newScrollHeight, clientHeight: newClientHeight } = eventsContainer;
                  const newScrolledPercentage = (newScrollTop + newClientHeight) / newScrollHeight;

                  // Only trigger another load if we're still at the bottom
                  if (newScrolledPercentage > 0.99 && this.hasMoreEvents && !isLoading) {
                    this.loadMoreEvents(false); // Do not show loader for auto-fetch
                  }
                }, MIN_REQUEST_INTERVAL * 1.5);
              }
            });
          }
        }, 150);
      });
    },

    async fetchFirewallData() {
      try {
        const response = await fetch('api/status/');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json() as FirewallStatusResponse;

        this.isProtectionEnabled = data.firewall_status.protection_enabled;
        this.isFullyConfigured = data.firewall_status.is_fully_configured;

        // Create a map of existing users to preserve their expanded state
        const existingUsersMap = new Map(
          this.users.map(user => [user.id, {
            showAllPackages: user.showAllPackages,
            packages: user.packages,
            isLoadingPackages: user.isLoadingPackages
          }])
        );

        const users: FirewallUser[] = data.users.map((user: any) => {
          const existingUser = existingUsersMap.get(user.email);
          return {
            id: user.email,
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            username: user.email.split('@')[0],
            is_protected: user.is_protected,
            package_managers: user.package_managers || [],
            package_manager_versions: user.package_manager_versions || {},
            last_seen: user.last_seen || 'Never',
            packages: existingUser?.packages || [],
            total_packages: user.total_count || 0,
            showAllPackages: existingUser?.showAllPackages || false,
            hasMorePackages: user.total_count > 0,
            isLoadingPackages: existingUser?.isLoadingPackages || false
          };
        });

        this.users = users;
        // Ensure this happens after the DOM update cycle where 'this.users' is populated
        this.$nextTick(() => {
            this.isLoadingUsers = false;
        });
        this.isLoading = false; // Overall loading can be set earlier
        this.hasData = true;

      } catch (error) {
        console.error('Error fetching firewall data:', error);
        // Ensure loading states are turned off even on error
        this.$nextTick(() => {
            this.isLoadingUsers = false;
        });
        this.isLoading = false;
      }
    },

    async fetchTelemetryEvents(reset = true, isPollingRefresh = false): Promise<void> {
      try {

        const searchTerm = this.currentSearchTerm;

        let apiOffset: number;
        if (reset) { // Full reset (initial load, filter change)
          this.eventsPage = 1;
          this.hasMoreEvents = true; // Reset hasMoreEvents on a full reset
          apiOffset = 0;
        } else if (isPollingRefresh) { // Background polling for newest events
          apiOffset = 0;
          // For polling, we don't change this.eventsPage for pagination state
        } else { // Pagination (load more)
          apiOffset = (this.eventsPage - 1) * this.eventsLimit;
        }

        const scrollLoader = document.getElementById('infinite-scroll-loader');
        // Show scroll loader only for initial load (reset) or pagination, not for background polling
        if (scrollLoader && (reset || (!isPollingRefresh && this.eventsPage > 1))) {
          scrollLoader.classList.remove('hidden');
        }

        // Construct the URL directly based on the request type
        let apiUrl = 'api/telemetry/events/?limit=' + this.eventsLimit.toString();

        // Only include offset for initial load or pagination, NOT for polling
        if (!isPollingRefresh) {
          apiUrl += '&offset=' + apiOffset.toString();
        }

        // Handle event type filters
        if (this.activeFilters.length > 0) {
          apiUrl += '&event_types=' + this.activeFilters.join(',');
        }

        console.log(`Fetching events: ${isPollingRefresh ? 'POLLING' : (reset ? 'RESET' : 'PAGINATION')}, URL: ${apiUrl}`);

        const response = await fetch(apiUrl);

        if (!response.ok) {
          // Handle 429 (Too Many Requests) gracefully during infinite scroll (pagination)
          if (response.status === 429 && !reset && !isPollingRefresh) {
            this.hasMoreEvents = false;
            const loadMoreButton = document.querySelector('[data-infinite-scroll="disabled"]');
            if (loadMoreButton) {
              loadMoreButton.classList.add('hidden');
            }
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && Array.isArray(data.results)) {
          // Group events by correlation ID for package.installed/uninstalled events
          const correlatedEvents = new Map<string, Array<Record<string, any>>>();
          const nonCorrelatedEvents: Array<Record<string, any>> = [];

          data.results.forEach((event: Record<string, any>) => {
            if (event.event_type && (event.event_type.includes('package.installed') || event.event_type.includes('package.uninstalled'))) {
              const correlationId = event.correlation_id || event.context?.correlation_id;
              if (correlationId) {
                if (!correlatedEvents.has(correlationId)) {
                  correlatedEvents.set(correlationId, []);
                }
                correlatedEvents.get(correlationId)?.push(event);
              } else {
                nonCorrelatedEvents.push(event);
              }
            } else {
              nonCorrelatedEvents.push(event);
            }
          });

          const summaryEvents: Array<Record<string, any>> = [];
          correlatedEvents.forEach((events, correlationId) => {
            events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            if (events.length === 1) {
              nonCorrelatedEvents.push(events[0]);
            } else {
              const firstEvent = events[0];
              let summaryEventType: string;
              let summaryEventTypeDisplay: string;
              if (firstEvent.event_type.includes('package.installed')) {
                summaryEventType = 'package.installed.summary';
                summaryEventTypeDisplay = 'Package Installation Summary';
              } else if (firstEvent.event_type.includes('package.uninstalled')) {
                summaryEventType = 'package.uninstalled.summary';
                summaryEventTypeDisplay = 'Package Uninstallation Summary';
              } else {
                summaryEventType = 'summary';
                summaryEventTypeDisplay = 'Event Summary';
              }
              const summaryEvent = {
                ...firstEvent,
                id: `summary-${correlationId}`,
                is_summary: true,
                summary_count: events.length,
                related_events: events,
                event_type: summaryEventType,
                event_type_display: summaryEventTypeDisplay
              };
              summaryEvents.push(summaryEvent);
            }
          });

          const allRawEvents = [...summaryEvents, ...nonCorrelatedEvents];
          allRawEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

          const packageOpCorrelationIds = new Set<string>();
          const packageOpEventTypes = [
            'package.installed', 'package.installed.summary',
            'package.uninstalled', 'package.uninstalled.summary',
            'package.updated', 'package.blocked',
          ];
          allRawEvents.forEach(event => {
            const eventType = event.event_type || '';
            const correlationId = event.correlation_id || event.context?.correlation_id;
            if (correlationId && packageOpEventTypes.some(opType => eventType.includes(opType))) {
              packageOpCorrelationIds.add(correlationId);
            }
          });

          const formattedEvents = allRawEvents
            .filter(event => {
              const eventType = event.event_type || '';
              const correlationId = event.correlation_id || event.context?.correlation_id;
              if (eventType.includes('command.executed') && correlationId && packageOpCorrelationIds.has(correlationId)) {
                return false;
              }
              return true;
            })
            .map((event: Record<string, any>): FirewallEvent | null => {
              const eventData = event.data || {};
              const eventContext = event.context || {};
              const eventType = event.event_type || '';
              const eventTypeDisplay = event.event_type_display || eventType.split('.').pop() || 'Event';
              let userName = this.escapeHtml(event.user?.name || 'Unknown user');
              const deviceName = this.escapeHtml(eventContext.runtime?.host?.name || '');
              const projectId = this.escapeHtml(eventContext.project?.id || '');
              const eventId = event.id || `api-${event.event_time}-${Math.random().toString(36).substring(2, 9)}`;

              // Skip logic for processedEventIds is handled during merging into this.updates

              let message = '';
              let displayType = '';
              let expandable = false;
              let packages: FirewallPackage[] = [];

              if (eventType.includes('package.installed.summary')) {
                displayType = 'success';
                const relatedEvents = event.related_events || [];
                const count = event.summary_count || relatedEvents.length;
                expandable = count > 1;
                const firstPackage = relatedEvents[0]?.package || {};
                const packageName = this.escapeHtml(firstPackage.name || 'Unknown package');
                const packageVersion = this.escapeHtml(firstPackage.version || 'Unknown version');
                const packageManager = this.escapeHtml(relatedEvents[0]?.data?.package_manager || firstPackage.package_manager || 'Unknown package manager');
                const project = this.escapeHtml(firstPackage.project || projectId || '');
                message = `<span class="font-medium">${userName}</span> installed <span class="font-medium">${packageName}</span>@<span class="text-green-600 dark:text-green-400 font-mono">${packageVersion}</span>`;
                if (count > 1) {
                  message += ` and <span class="font-medium">${count - 1}</span> other package${count - 1 > 1 ? 's' : ''}`;
                }
                message += ` using ${packageManager}`;
                if (project) message += ` in project <span class="font-medium">${project}</span>`;
                packages = relatedEvents.map((e: any) => ({
                  name: this.escapeHtml(e.package?.name || 'Unknown'),
                  version: this.escapeHtml(e.package?.version || 'Unknown'),
                  package_manager: this.escapeHtml(e.data?.package_manager || e.package?.package_manager || 'Unknown'),
                  project: this.escapeHtml(e.package?.project || projectId || ''),
                  install_path: this.escapeHtml(e.package?.install_path || ''),
                  installed_at: e.timestamp
                }));
              }
              else if (eventType.includes('package.uninstalled.summary')) {
                displayType = 'info';
                const relatedEvents = event.related_events || [];
                const count = event.summary_count || relatedEvents.length;
                expandable = count > 1;
                const firstPackage = relatedEvents[0]?.package || {};
                const packageName = this.escapeHtml(firstPackage.name || relatedEvents[0]?.data?.package_name || 'Unknown package');
                const packageVersion = this.escapeHtml(firstPackage.version || relatedEvents[0]?.data?.version || 'Unknown version');
                const packageManager = this.escapeHtml(relatedEvents[0]?.data?.tool || relatedEvents[0]?.data?.package_manager || firstPackage.package_manager || 'Unknown package manager');
                const project = this.escapeHtml(firstPackage.project || projectId || '');
                message = `<span class="font-medium">${userName}</span> uninstalled <span class="font-medium">${packageName}</span>@<span class="text-gray-700 dark:text-gray-300 font-mono">${packageVersion}</span>`;
                if (count > 1) {
                  message += ` and <span class="font-medium">${count - 1}</span> other package${count - 1 > 1 ? 's' : ''}`;
                }
                message += ` using ${packageManager}`;
                if (project) message += ` from project <span class="font-medium">${project}</span>`;
                packages = relatedEvents.map((e: any) => ({
                  name: this.escapeHtml(e.package?.name || e.data?.package_name || 'Unknown'),
                  version: this.escapeHtml(e.package?.version || e.data?.version || 'Unknown'),
                  package_manager: this.escapeHtml(e.data?.tool || e.data?.package_manager || e.package?.package_manager || 'Unknown'),
                  project: this.escapeHtml(e.package?.project || projectId || ''),
                  install_path: this.escapeHtml(e.package?.install_path || ''),
                  installed_at: e.timestamp
                }));
              }
              else if (eventType.includes('package.installed')) {
                displayType = 'success';
                expandable = false;
                const packageInfo = event.package || {};
                const packageName = this.escapeHtml(packageInfo.name || 'Unknown package');
                const packageVersion = this.escapeHtml(packageInfo.version || 'Unknown version');
                const packageManager = this.escapeHtml(event.data?.package_manager || packageInfo.package_manager || 'Unknown package manager');
                const project = this.escapeHtml(packageInfo.project || projectId || '');
                message = `<span class="font-medium">${userName}</span> installed <span class="font-medium">${packageName}</span>@<span class="text-green-600 dark:text-green-400 font-mono">${packageVersion}</span> using ${packageManager}`;
                if (project) message += ` in project <span class="font-medium">${project}</span>`;
                packages = [{
                  name: packageName, version: packageVersion, package_manager: packageManager,
                  project: project, install_path: this.escapeHtml(packageInfo.install_path || ''), installed_at: event.timestamp
                }];
              }
              else if (eventType.includes('package.blocked')) {
                displayType = 'blocked';
                const packageInfo = event.package || {};
                const packageName = this.escapeHtml(packageInfo.name || event.data?.package_name || 'Unknown package');
                const packageVersion = this.escapeHtml(packageInfo.version || event.data?.version || 'Unknown version');
                const packageManager = this.escapeHtml(event.data?.package_manager || packageInfo.package_manager || 'Unknown package manager');
                const project = this.escapeHtml(packageInfo.project || projectId || '');
                const reason = this.escapeHtml(packageInfo.reason || event.data?.reason || '');
                message = `<span class="font-medium">Safety</span> blocked ${userName} from installing <span class="font-medium">${packageName}</span>@<span class="text-gray-700 dark:text-gray-300 font-mono">${packageVersion}</span> via ${packageManager}`;
                if (project) message += ` in project <span class="font-medium">${project}</span>`;
                if (reason) message += `<br><span class="text-sm text-red-600 dark:text-red-400 mt-1 block">Reason: ${reason}</span>`;
              }
              else if (eventType.includes('package.uninstalled')) {
                displayType = 'info';
                const packageInfo = event.package || {};
                const packageName = this.escapeHtml(packageInfo.name || event.data?.package_name || 'Unknown package');
                const packageVersion = this.escapeHtml(packageInfo.version || event.data?.version || 'Unknown version');
                const packageManager = this.escapeHtml(event.data?.tool || event.data?.package_manager || packageInfo.package_manager || 'Unknown package manager');
                const project = this.escapeHtml(packageInfo.project || projectId || '');
                expandable = false;
                message = `<span class="font-medium">${userName}</span> uninstalled <span class="font-medium">${packageName}</span>@<span class="text-gray-700 dark:text-gray-300 font-mono">${packageVersion}</span> using ${packageManager}`;
                if (project) message += ` from project <span class="font-medium">${project}</span>`;
                packages = [{
                  name: packageName, version: packageVersion, package_manager: packageManager,
                  project: project, install_path: this.escapeHtml(packageInfo.install_path || ''), installed_at: event.timestamp
                }];
              }
              else if (eventType.includes('package.updated')) {
                displayType = 'success';
                const packageInfo = event.package || {};
                const packageName = this.escapeHtml(packageInfo.name || event.data?.package_name || 'Unknown package');
                const packageVersion = this.escapeHtml(packageInfo.version || event.data?.current_version || event.data?.version || 'Unknown version');
                const previousVersion = this.escapeHtml(packageInfo.previous_version || event.data?.previous_version || '');
                const packageManager = this.escapeHtml(event.data?.package_manager || packageInfo.package_manager || 'Unknown package manager');
                const project = this.escapeHtml(packageInfo.project || projectId || '');
                message = `<span class="font-medium">${userName}</span> updated <span class="font-medium">${packageName}</span>`;
                if (previousVersion) message += ` from <span class="text-gray-700 dark:text-gray-300 font-mono">${previousVersion}</span>`;
                message += ` to <span class="text-green-600 dark:text-green-400 font-mono">${packageVersion}</span> using ${packageManager}`;
                if (project) message += ` in project <span class="font-medium">${project}</span>`;
              }
              else if (eventType.includes('firewall.configured')) {
                displayType = 'success';
                message = `<span class="font-medium">${userName}</span> configured Safety Firewall`;
              }
              else if (eventType.includes('firewall.disabled')) {
                displayType = 'error';
                const reason = this.escapeHtml(event.data?.reason || '');
                message = `<span class="font-medium">${userName}</span> disabled Safety Firewall`;
                if (reason) message += `<br><span class="text-xs text-gray-500 mt-1 block">Reason: ${reason}</span>`;
              }
              else if (eventType.includes('firewall.heartbeat')) {
                displayType = 'info';
                message = `Safety Firewall heartbeat from <span class="font-medium">${userName}</span>`;
                if (deviceName) message += ` on device <span class="font-medium">${deviceName}</span>`;
              }
              else if (eventType.includes('command.executed')) {
                displayType = 'success';
                const fullCommand = this.formatRawCommand(event.data?.raw_command || []);
                message = `<span class="font-medium">${userName}</span> executed command: <code class="px-1 text-sm font-mono bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded border border-gray-200 dark:border-gray-700">${fullCommand}</code>`;
              }
              else if (eventType.includes('command.error')) {
                displayType = 'error';
                const commandName = this.escapeHtml(event.data?.command_name || 'Unknown command');
                const errorMessage = this.escapeHtml(event.data?.error_message);
                const fullCommand = this.formatRawCommand(event.data?.raw_command || []);
                message = `<span class="font-medium">${userName}</span> encountered an error with command: <code class="px-1 text-xs font-mono bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded border border-gray-200 dark:border-gray-700">${fullCommand || `safety ${commandName}`}</code>`;
                if (errorMessage) message += `<br><span class="text-sm text-red-600 dark:text-red-400 mt-1 block">Error: ${errorMessage}</span>`;
              }
              else {
                message = `${eventTypeDisplay} by <span class="font-medium">${userName}</span>`;
              }

              return {
                type: displayType,
                message,
                timestamp: event.timestamp,
                eventId,
                isNew: !reset && isPollingRefresh && !this.processedEventIds.has(eventId), // isNew only for polling refreshes of genuinely new events
                expandable,
                expanded: false,
                packages,
                raw_event: {
                  data: eventData, context: eventContext, event_type: eventType,
                  event_type_display: eventTypeDisplay, source: event.source || 'unknown', timestamp: event.timestamp
                }
              } as FirewallEvent;
            }).filter((event): event is FirewallEvent => event !== null);

          this.hasMoreEvents = !!data.next || data.results.length === this.eventsLimit;

          if (reset) {
            this.updates = formattedEvents;
            this.processedEventIds.clear(); // Clear for full reset
            formattedEvents.forEach(event => this.processedEventIds.add(event.eventId));
          } else if (isPollingRefresh) {
            const existingEventIds = new Set(this.updates.map((event: FirewallEvent) => event.eventId));
            const uniqueNewEvents = formattedEvents.filter((event: FirewallEvent) => !existingEventIds.has(event.eventId));
            if (uniqueNewEvents.length > 0) {
              this.updates = [...uniqueNewEvents, ...this.updates];
              uniqueNewEvents.forEach(event => this.processedEventIds.add(event.eventId));
              this.notifyNewEvent();
            }
          } else { // Pagination
             // For pagination, filter against already processed IDs to avoid duplicates if API overlaps
            const uniqueNewEvents = formattedEvents.filter(event => !this.processedEventIds.has(event.eventId));
            this.updates = [...this.updates, ...uniqueNewEvents];
            uniqueNewEvents.forEach(event => this.processedEventIds.add(event.eventId));
          }

          this.applyEventTypeFilters();

          const loadMoreButton = document.querySelector('[data-infinite-scroll="disabled"]');
          if (loadMoreButton) {
            if (!this.hasMoreEvents) {
              loadMoreButton.classList.add('hidden');
            } else {
              loadMoreButton.classList.remove('hidden');
            }
          }
        } else {
          this.hasMoreEvents = false;
          if (!reset && !isPollingRefresh) { // Only hide load more button if it's a pagination call that returned no more
            const loadMoreButton = document.querySelector('[data-infinite-scroll="disabled"]');
            if (loadMoreButton) {
              loadMoreButton.classList.add('hidden');
            }
          }
        }

        if (reset || isPollingRefresh) { // Check initial scroll only on reset or polling, not pagination
          this.checkInitialScrollAndLoadMore();
        }

        if (searchTerm) {
          setTimeout(() => {
            this.users.forEach(user => {
              const detailsRow = document.getElementById(`${user.id}-details`);
              if (detailsRow && !detailsRow.classList.contains('hidden')) {
                this.highlightMatchingPackages(user.id, searchTerm);
              }
            });
          }, 100);
        }

        if (isPollingRefresh && searchTerm) { // Updated condition to use isPollingRefresh
          setTimeout(() => {
            document.querySelectorAll('.preserved-highlight').forEach(el => {
              el.classList.remove('preserved-highlight');
            });
            document.querySelectorAll('tr:not(.highlighted-row)').forEach((row) => {
              if (row.classList.contains('see-more-row') || row.classList.contains('no-matches-row')) return;
              row.classList.add('highlighted-row');
              let rowHasMatch = false;
              row.querySelectorAll('td').forEach(cell => {
                if (cell.querySelector('.search-highlight')) return;
                const content = cell.textContent || '';
                if (content.toLowerCase().includes(searchTerm.toLowerCase())) {
                  rowHasMatch = true;
                  if (!cell.hasAttribute('data-original')) cell.setAttribute('data-original', content);
                  const highlightedContent = content.replace(
                    new RegExp(this.escapeHtml(searchTerm).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
                    (match: string) => `<span class="search-highlight">${this.escapeHtml(match)}</span>`
                  );
                  cell.innerHTML = highlightedContent;
                }
              });
              if (rowHasMatch) {
                (row as HTMLElement).style.display = 'table-row';
                row.classList.add('flash-highlight');
              }
            });
          }, 100);
        }

      } catch (error: unknown) {
        console.error('Error fetching telemetry events:', error);
        this.hasMoreEvents = false;
        this.isLoadingEvents = false;
        const eventsContainer = document.querySelector('.firewall-events-scroll');
        if (eventsContainer) {
          const errorElement = document.createElement('div');
          errorElement.className = 'text-red-500 text-sm p-2 bg-red-50 rounded border border-red-200 mb-2 fade-out';
          errorElement.textContent = 'Failed to refresh events. Will try again shortly.';
          eventsContainer.prepend(errorElement);
          setTimeout(() => { errorElement.remove(); }, 5000);
        }
      } finally {
        this.isLoadingEvents = false;
        const scrollLoader = document.getElementById('infinite-scroll-loader');
        // Hide scroll loader if it was shown (not for background polling unless it was a reset)
        if (scrollLoader && (reset || (!isPollingRefresh && this.eventsPage > 1))) {
          scrollLoader.classList.add('hidden');
        }
      }
    },

    async loadMoreEvents(showLoader: boolean = true): Promise<void> {
      // Prevent concurrent requests
      if (this.isLoadingMore) {
        return;
      }

      try {
        const scrollLoader = document.getElementById('infinite-scroll-loader');
        if (showLoader && scrollLoader) {
          scrollLoader.classList.remove('hidden');
        }

        this.eventsPage++;
        this.isLoadingMore = true;

        await this.fetchTelemetryEvents(false); // isPollingRefresh defaults to false, correct for pagination

        // If we got less than half of the expected events due to filtering/grouping
        // and there may be more events available, immediately try to load more
        const minExpectedEvents = this.eventsLimit / 2;
        if (this.hasMoreEvents && this.updates.length < minExpectedEvents) {
          // Add a slight delay before requesting more
          setTimeout(() => {
            if (this.hasMoreEvents && !this.isLoadingMore) {
              this.loadMoreEvents(false); // Do not show loader for auto-fetch
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error loading more events:', error);
      } finally {
        this.isLoadingMore = false;
      }
    },

    resetEventTypeFilters() {
      this.eventTypeFilters = {
        success: true,
        error: true,
        info: true,
        warning: true,
        blocked: true
      };
    },

    applyEventTypeFilters() {
      if (!Array.isArray(this.updates)) {
        this.updates = [];
        return;
      }

      const enabledTypes = Object.entries(this.eventTypeFilters || {})
        .filter(([_, enabled]) => enabled)
        .map(([type]) => type);

      const filteredUpdates = this.updates.filter((update: FirewallEvent) =>
        update && update.type && enabledTypes.includes(update.type)
      );

      if (filteredUpdates.length === 0 && this.updates.length > 0) {
        this.updates.forEach(update => {
          if (!update.type || !enabledTypes.includes(update.type)) {
            update.type = 'info';
          }
        });
      } else {
        this.updates = filteredUpdates;
      }

      this.activeFilters = enabledTypes;
    },

    async refreshTelemetryEvents(): Promise<void> {
      await this.fetchTelemetryEvents(false, true); // Explicitly polling refresh
    },

    getCsrfToken(): string {
      return document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1] || '';
    },

    toggleUserDetails(userId: string): void {
      const user = this.users.find(u => u.id === userId);
      if (!user) return;

      const row = document.getElementById(`${userId}-details`);
      const chevron = document.getElementById(`${userId}-chevron`);

      if (!row) return;

      const isHidden = row.classList.contains('hidden');
      row.classList.toggle('hidden');

      if (chevron) {
        chevron.style.transform = isHidden ? 'rotate(90deg)' : '';
      }

      // Load packages for all users when expanding their details
      if (isHidden) {
        const tableBody = document.getElementById(`${userId}-packages-tbody`);

        if (tableBody) {
          tableBody.innerHTML = `
            <tr class="animate-pulse">
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-3/4"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/4"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/2"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/4"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/2"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/4"></div></td>
            </tr>
            <tr class="animate-pulse">
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-2/3"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/3"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/2"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/3"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-2/3"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/3"></div></td>
            </tr>
            <tr class="animate-pulse">
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/2"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/4"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-2/3"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/4"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/2"></div></td>
              <td class="px-4 py-2"><div class="h-4 bg-gray-200 rounded w-1/4"></div></td>
            </tr>
          `;
        }

        this.loadUserPackages(user.email);
      }
    },

    togglePackagesVisibility(userId: string): void {
      const user = this.users.find((u: FirewallUser) => u.id === userId);
      if (!user) return;

      user.showAllPackages = !user.showAllPackages;

      const tableBody = document.getElementById(`${userId}-packages-tbody`);
      if (tableBody) {
        tableBody.style.opacity = '0';
        tableBody.style.transition = 'opacity 0.15s ease';

        const searchInput = document.getElementById('search-input') as HTMLInputElement;
        const searchTerm = searchInput?.value.toLowerCase() || '';

        setTimeout(() => {
          tableBody.innerHTML = this.renderPackagesTable(user);

          void tableBody.offsetHeight;

          tableBody.style.opacity = '1';

          setTimeout(() => {
            if (searchTerm) {
              this.highlightMatchingPackages(userId, searchTerm);

              setTimeout(() => {
                const hasHighlighting = tableBody.querySelector('.search-highlight');
                if (!hasHighlighting && searchTerm) {
                  this.highlightMatchingPackages(userId, searchTerm);
                }
              }, 100);
            }
          }, 150);
        }, 50);
      }
    },

    processSearchResults(data: any[], searchTerm: string): void {
      const packagesByUser: Record<string, any[]> = {};
      data.forEach((pkg: any) => {
        if (pkg.user_email) {
          if (!packagesByUser[pkg.user_email]) {
            packagesByUser[pkg.user_email] = [];
          }
          packagesByUser[pkg.user_email].push(pkg);
        }
      });

      this.users.forEach(user => {
        const userRow = document.querySelector(`[data-user-id="${user.id}"]`);
        if (userRow) {
          userRow.classList.remove('loading');
        }

        const tableBody = document.getElementById(`${user.id}-packages-tbody`);
        if (!tableBody) return;

        const userPackages = packagesByUser[user.email] || [];

        if (userPackages.length > 0) {
          if (userRow) {
            const userRowElement = userRow as HTMLElement;
            userRowElement.style.display = '';
            userRowElement.style.opacity = '1';
            userRow.classList.add('row-with-match');
          }

          const detailsRow = document.getElementById(`${user.id}-details`);
          const chevron = document.getElementById(`${user.id}-chevron`);
          if (detailsRow) {
            detailsRow.classList.remove('hidden');
            detailsRow.classList.add('visible-with-transition');

            if (chevron) {
              chevron.style.transform = 'rotate(90deg)';
            }
          }

          // For search results, always show all packages without pagination
          const thisUser = this.users.find(u => u.id === user.id);
          if (thisUser) {
            thisUser.showAllPackages = true;
          }

          const packageRows = userPackages.map((pkg: any) => `
            <tr>
              <td class="px-4 py-2 name-cell">${this.escapeHtml(pkg.name || 'Unknown')}</td>
              <td class="px-4 py-2">${this.escapeHtml(pkg.version || 'Unknown')}</td>
              <td class="px-4 py-2">${this.escapeHtml(pkg.project || 'Unknown')}</td>
              <td class="px-4 py-2">${this.escapeHtml(pkg.package_manager || 'Unknown')}</td>
              <td class="px-4 py-2">${this.escapeHtml(pkg.install_path || 'Unknown')}</td>
              <td class="px-4 py-2">${this.getRelativeTime(pkg.installed_at) || 'Never'}</td>
            </tr>
          `).join('');

          tableBody.innerHTML = packageRows;

          const nameCells = tableBody.querySelectorAll('.name-cell');
          nameCells.forEach(cell => {
            const content = cell.textContent || '';
            const lowerContent = content.toLowerCase();
            const lowerSearchTerm = searchTerm.toLowerCase();

            if (lowerContent.includes(lowerSearchTerm)) {
              const highlightedContent = content.replace(
                new RegExp(this.escapeHtml(searchTerm).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                'gi'),
                (match: string) => `<span class="search-highlight">${this.escapeHtml(match)}</span>`
              );
              cell.innerHTML = highlightedContent;
            }
          });
        } else {
          const originalContent = tableBody.getAttribute('data-original-content');
          if (originalContent) {
            tableBody.innerHTML = originalContent;
          } else {
            tableBody.innerHTML = '';
          }
        }
      });
    },

    filterUsers(): void {
      // Don't proceed with search if users are still loading
      if (this.isLoadingUsers) {
        return;
      }

      const searchInput = document.querySelector<HTMLInputElement>('input[placeholder*="Search"]');
      if (!searchInput) {
        console.warn('Search input not found');
        return;
      }

      // First, remove any existing loading indicators to prevent duplicates
      const existingLoadingIndicator = searchInput.parentElement?.querySelector('.search-loading-indicator');
      if (existingLoadingIndicator) {
        existingLoadingIndicator.remove();
      }

      const searchTerm = searchInput.value.trim().toLowerCase();
      this.currentSearchTerm = searchTerm; // Store the current search term

      // Update clear button visibility
      if (searchTerm) {
        this.addSearchClearButton(searchInput);
      } else {
        this.removeSearchClearButton(searchInput);
      }

      // Reset search UI when the search term is empty
      if (!searchTerm) {
        this.resetHighlights(); // Don't pass parameter, use default value

        // Reset all user rows and make sure they're visible
        this.users.forEach(user => {
          // Mark packages as needing refresh when a user dropdown is expanded
          user.packages = [];

          const userRow = document.querySelector(`[data-user-id="${user.id}"]`);
          if (userRow) {
            (userRow as HTMLElement).style.display = '';
            (userRow as HTMLElement).style.opacity = '1';
            userRow.classList.remove('row-with-match');
          }

          const detailsRow = document.getElementById(`${user.id}-details`);
          if (detailsRow) {
            detailsRow.classList.add('hidden');
            detailsRow.classList.remove('visible-with-transition');
            detailsRow.classList.remove('expanded-with-matches');
          }

          const chevron = document.getElementById(`${user.id}-chevron`);
          if (chevron) {
            chevron.style.transform = '';
          }
        });

        return;
      }

      // Hide all user rows by default for search
      this.users.forEach(user => {
        const userRow = document.querySelector(`[data-user-id="${user.id}"]`);
        if (userRow) {
          (userRow as HTMLElement).style.display = 'none';
          (userRow as HTMLElement).style.opacity = '0';
          userRow.classList.remove('row-with-match');
        }
      });

      // Track search stats
      const usersWithPackageMatches = new Set<string>();

      // First check in the currently loaded data
      this.users.forEach(user => {
        const userId = user.id;
        const userRow = document.querySelector(`[data-user-id="${userId}"]`);
        if (!userRow) return;

        // Check for matches in user info
        const userInfo = `${user.first_name} ${user.last_name} ${user.email}`.toLowerCase();
        const userMatches = userInfo.includes(searchTerm);

        if (userMatches) {
          (userRow as HTMLElement).style.display = '';
          (userRow as HTMLElement).style.opacity = '1';
        }

        if (user.packages && user.packages.length > 0) {
          let packageMatches = false;

          user.packages.forEach(pkg => {
            if (
              (pkg.name && pkg.name.toLowerCase().includes(searchTerm)) ||
              (pkg.version && pkg.version.toLowerCase().includes(searchTerm)) ||
              (pkg.project && pkg.project.toLowerCase().includes(searchTerm)) ||
              (pkg.package_manager && pkg.package_manager.toLowerCase().includes(searchTerm)) ||
              (pkg.install_path && pkg.install_path.toLowerCase().includes(searchTerm)) ||
              (pkg.installed_at && pkg.installed_at.toLowerCase().includes(searchTerm))
            ) {
              packageMatches = true;
              usersWithPackageMatches.add(userId);
            }
          });

          if (packageMatches) {
            (userRow as HTMLElement).style.display = '';
            (userRow as HTMLElement).style.opacity = '1';
            userRow.classList.add('row-with-match');

            const detailsRow = document.getElementById(`${userId}-details`);
            const chevron = document.getElementById(`${userId}-chevron`);

            if (detailsRow) {
              detailsRow.classList.remove('hidden');
              detailsRow.classList.remove('hidden-with-transition');
              detailsRow.classList.add('visible-with-transition');
              detailsRow.classList.add('expanded-with-matches');

              if (chevron) {
                chevron.style.transform = 'rotate(90deg)';
              }

              const packagesTable = detailsRow.querySelector('table');
              const packagesBody = document.getElementById(`${userId}-packages-tbody`);

              if (packagesTable && packagesBody) {
                (packagesTable as HTMLElement).style.display = 'table';
                (packagesBody as HTMLElement).style.display = 'table-row-group';

                this.highlightMatchingPackages(userId, searchTerm);
              }
            }
          }
        }
      });

      if (this.searchDebounceTimer) {
        window.clearTimeout(this.searchDebounceTimer);
      }

      // Function to ensure loading indicator is removed
      const removeLoadingIndicator = () => {
        const indicator = searchInput.parentElement?.querySelector('.search-loading-indicator');
        if (indicator) {
          indicator.remove();
        }
      };

      this.searchDebounceTimer = window.setTimeout(async () => {
        try {
          // Check if we have cached results for this exact search term
          if (this.searchCache.has(searchTerm) && searchTerm.length >= 3) {
            const cachedData = this.searchCache.get(searchTerm);
            if (cachedData) {
              // Process cached results without API call
              this.processSearchResults(cachedData, searchTerm);
              removeLoadingIndicator();
              return;
            }
          }

          // Only trigger API search if we have at least 3 characters
          if (searchTerm.length >= 3) {
            // Make API request without showing any loading skeletons
            const response = await fetch(`api/search-packages/?q=${encodeURIComponent(searchTerm)}`);

            if (response.status === 429) {
              throw new Error('Rate limit exceeded. Please wait a moment before searching again.');
            }

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data && Array.isArray(data)) {
              const packagesByUser: Record<string, any[]> = {};
              data.forEach((pkg: any) => {
                if (pkg.user_email) {
                  if (!packagesByUser[pkg.user_email]) {
                    packagesByUser[pkg.user_email] = [];
                  }
                  packagesByUser[pkg.user_email].push(pkg);
                }
              });

              const userPackagesMap = new Map<string, Set<string>>();

              this.users.forEach(user => {
                const packageKeys = new Set<string>();

                user.packages.forEach(pkg => {
                  const key = `${pkg.name}|${pkg.version}|${pkg.project}|${pkg.package_manager}|${pkg.install_path}`;
                  packageKeys.add(key);
                });

                userPackagesMap.set(user.email, packageKeys);
              });

              let updatedUsers = false;
              this.users = this.users.map(user => {
                const packageKeys = userPackagesMap.get(user.email) || new Set<string>();

                const userPackages = packagesByUser[user.email] || [];

                if (userPackages.length > 0) {
                  const newPackages = userPackages.map((p: any) => ({
                    name: p.name,
                    version: p.version || 'Unknown',
                    project: p.project || 'Unknown',
                    package_manager: p.package_manager || 'Unknown',
                    install_path: p.install_path || 'Unknown',
                    installed_at: p.installed_at  // Send raw timestamp
                  }));

                  const uniqueNewPackages = newPackages.filter((pkg: FirewallPackage) => {
                    const key = `${pkg.name}|${pkg.version}|${pkg.project}|${pkg.package_manager}|${pkg.install_path}`;
                    if (packageKeys.has(key)) {
                      return false;
                    }
                    packageKeys.add(key);
                    return true;
                  });

                  if (uniqueNewPackages.length > 0) {
                    updatedUsers = true;
                    return {
                      ...user,
                      packages: [...user.packages, ...uniqueNewPackages]
                    };
                  }
                }
                return user;
              });

              if (updatedUsers) {
                this.$nextTick(() => {
                  this.users.forEach(user => {
                    if (packagesByUser[user.email]?.length > 0) {
                      const userRow = document.querySelector(`[data-user-id="${user.id}"]`) as HTMLElement;
                      if (userRow) {
                        userRow.style.display = '';
                        userRow.style.opacity = '1';
                        userRow.classList.add('row-with-match');
                      }

                      const detailsRow = document.getElementById(`${user.id}-details`);
                      const chevron = document.getElementById(`${user.id}-chevron`);

                      if (detailsRow) {
                        detailsRow.classList.remove('hidden');
                        detailsRow.style.display = '';

                        if (chevron) {
                          chevron.style.transform = 'rotate(90deg)';
                        }
                      }

                      const tableBody = document.getElementById(`${user.id}-packages-tbody`);
                      if (tableBody) {
                        tableBody.innerHTML = this.renderPackagesTable(user);

                        if (searchTerm) {
                          this.highlightMatchingPackages(user.id, searchTerm);
                        }
                      }
                    }
                  });
                });
              }

              // Cache the results for future use
              this.searchCache.set(searchTerm, data);
            }
          }
        } catch (error) {
          console.error('Error in search debounce:', error);
          // Show error message to user
          const errorMessage = document.createElement('div');
          errorMessage.className = 'search-error-message text-red-500 text-sm mt-1';
          errorMessage.textContent = error instanceof Error ? error.message : 'An error occurred during search';
          searchInput.parentElement?.appendChild(errorMessage);

          // Remove error message after 3 seconds
          setTimeout(() => {
            errorMessage.remove();
          }, 3000);
        }
      }, 500); // Increased debounce time to 500ms
    },

    // Add helper methods for search clear button that don't need to be part of the interface
    addSearchClearButton(searchInput: HTMLInputElement): void {
      if (!searchInput.parentElement) return;

      // Don't add if it already exists
      if (searchInput.parentElement.querySelector('.search-clear-button')) return;

      const clearButton = document.createElement('button');
      clearButton.className = 'search-clear-button absolute inset-y-0 right-0 pr-3 flex items-center';
      clearButton.innerHTML = `
        <svg class="h-5 w-5 text-gray-400 hover:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
      `;

      // Use a variable to capture the current context
      const component = this;

      // Add event listener to clear the search when clicked
      clearButton.addEventListener('click', function() {
        searchInput.value = '';
        component.currentSearchTerm = '';
        component.filterUsers();
        clearButton.remove();
      });

      searchInput.parentElement.appendChild(clearButton);
    },

    removeSearchClearButton(searchInput: HTMLInputElement): void {
      if (!searchInput.parentElement) return;

      const clearButton = searchInput.parentElement.querySelector('.search-clear-button');
      if (clearButton) {
        clearButton.remove();
      }
    },

    highlightMatchingPackages(userId: string, searchTerm: string): void {
      if (!userId || !searchTerm) return;

      const tableBody = document.getElementById(`${userId}-packages-tbody`);
      if (!tableBody) {
        console.warn(`Cannot find packages table body for user ${userId}`);
        return;
      }

      tableBody.style.display = 'table-row-group';

      const existingNoMatchesRow = tableBody.querySelector('.no-matches-row');
      if (existingNoMatchesRow) {
        existingNoMatchesRow.remove();
      }

      const packageRows = tableBody.querySelectorAll('tr:not(.see-more-row):not(.no-matches-row)');
      let matchCount = 0;

      packageRows.forEach(row => {
        let rowHasMatch = false;

        row.querySelectorAll('td').forEach(cell => {
          const content = cell.textContent || '';

          if (content.toLowerCase().includes(searchTerm.toLowerCase())) {
            rowHasMatch = true;
            matchCount++;

            if (!cell.hasAttribute('data-original')) {
              cell.setAttribute('data-original', content);
            }

            const highlightedContent = content.replace(
              new RegExp(this.escapeHtml(searchTerm).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
              (match: string) => `<span class="search-highlight">${this.escapeHtml(match)}</span>`
            );

            cell.innerHTML = highlightedContent;
          }
        });

        if (rowHasMatch) {
          (row as HTMLElement).style.display = 'table-row';
          row.classList.add('flash-highlight');
        } else {
          (row as HTMLElement).style.display = 'none';
        }
      });

      if (matchCount === 0) {
        const noMatchesRow = document.createElement('tr');
        noMatchesRow.className = 'no-matches-row';
        noMatchesRow.innerHTML = `
          <td colspan="6" class="px-4 py-2 text-center text-gray-500">
            No matches found for "${this.escapeHtml(searchTerm)}" in this user's packages.
          </td>
        `;
        tableBody.appendChild(noMatchesRow);
      }
    },

    async searchPackages(searchTerm: string): Promise<Array<{ user_email: string; package: FirewallPackage }>> {
      return new Promise(async (resolve, reject) => {
        try {

          // Check if we have cached results for this exact search term
          if (this.searchCache.has(searchTerm)) {
            const cachedData = this.searchCache.get(searchTerm);
            if (!cachedData) {
              reject(new Error('Cached data is undefined'));
              return;
            }

            // If we have cached results, process them directly without showing loading indicators
            this.processSearchResults(cachedData, searchTerm);

            // Return the cached data
            resolve(cachedData.map((p: any) => ({
              user_email: p.user_email,
              package: {
                name: p.name,
                version: p.version || 'Unknown',
                project: p.project || 'Unknown',
                package_manager: p.package_manager || 'Unknown',
                install_path: p.install_path || 'Unknown',
                installed_at: p.installed_at  // Send raw timestamp
              }
            })));
            return;
          }

          // Make API request without showing any loading skeletons
          const response = await fetch(`api/search-packages/?q=${encodeURIComponent(searchTerm)}`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          // Cache the results
          this.searchCache.set(searchTerm, data);

          // Process the search results
          this.processSearchResults(data, searchTerm);

          resolve(data.map((p: any) => ({
            user_email: p.user_email,
            package: {
              name: p.name,
              version: p.version || 'Unknown',
              project: p.project || 'Unknown',
              package_manager: p.package_manager || 'Unknown',
              install_path: p.install_path || 'Unknown',
              installed_at: p.installed_at  // Send raw timestamp
            }
          })));
        } catch (error) {
          console.error('Error searching packages:', error);

          this.users.forEach(user => {
            const userRow = document.querySelector(`[data-user-id="${user.id}"]`);
            if (userRow) {
              userRow.classList.remove('loading');
            }

            const tableBody = document.getElementById(`${user.id}-packages-tbody`);
            if (tableBody) {
              const originalContent = tableBody.getAttribute('data-original-content');
              if (originalContent) {
                tableBody.innerHTML = originalContent;
                tableBody.removeAttribute('data-original-content');
              }
            }
          });

          reject(error);
        }
      });
    },

    handleFirewallUpdate(data: FirewallStatusResponse): void {
      if (!data) return;

      // If we have an active search, don't refresh the package tables as it causes flickering
      const hasActiveSearch = this.currentSearchTerm && this.currentSearchTerm.trim() !== '';

      if (data.firewall_status) {
        this.isProtectionEnabled = data.firewall_status.protection_enabled;
        this.isFullyConfigured = data.firewall_status.is_fully_configured;
      }

      // Only update users data if we're not in the middle of a search
      if (data.users && !hasActiveSearch) {
        const users: FirewallUser[] = data.users.map((user: any) => {
          const existingUser = this.users.find(u => u.email === user.email);
          return {
            id: user.email,
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            username: user.email.split('@')[0],
            is_protected: user.is_protected,
            package_managers: user.package_managers || [],
            package_manager_versions: user.package_manager_versions || {},
            last_seen: user.last_seen || 'Never',
            packages: existingUser ? [...existingUser.packages].sort((a, b) => new Date(b.installed_at).getTime() - new Date(a.installed_at).getTime()) : [],
            total_packages: user.total_count || 0,
            showAllPackages: existingUser ? existingUser.showAllPackages : false,
            hasMorePackages: user.total_count > 0,
            isLoadingPackages: false
          };
        });

        this.users = users;
      }

      this.isLoading = false;
      this.hasData = true;

      // Skip package installation events processing during search
      if (hasActiveSearch) return;

      // Process package installation events only if not in search mode
      for (const event of this.updates) {
        // Check if this is a package installation event by looking for patterns in the message
        if (event.type === 'success' &&
            event.message.includes('installed') &&
            event.message.includes('using') &&
            (event.message.includes('and') || event.message.includes('other package'))) {

          event.expandable = true;
          event.expanded = false;

          // Try to extract package information from the raw event data
          if (event.raw_event?.data?.packages) {
            event.packages = Array.isArray(event.raw_event.data.packages)
              ? event.raw_event.data.packages
              : [event.raw_event.data.packages];
          }
          // If packages aren't explicitly provided, try to extract from the event data
          else if (event.raw_event?.data?.package_name && event.raw_event?.data?.package_version) {
            // Create a basic package entry from the available data
            const pkg: FirewallPackage = {
              name: event.raw_event.data.package_name,
              version: event.raw_event.data.package_version,
              package_manager: event.raw_event.data.package_manager || '',
              project: event.raw_event.data.project || '',
              install_path: event.raw_event.data.install_path || '',
              installed_at: event.timestamp
            };

            event.packages = [pkg];

            // Try to parse additional packages mentioned in the message
            if (event.message.includes('and')) {
              // This is a rough heuristic to extract the number of other packages
              const match = event.message.match(/and\s+(\d+)\s+other\s+packages?/i);
              if (match && match[1]) {
                const otherPackagesCount = parseInt(match[1], 10);
                // If there are other packages mentioned but not detailed, add placeholders
                if (otherPackagesCount > 0 && event.raw_event?.data?.other_packages) {
                  event.packages = [...event.packages, ...event.raw_event.data.other_packages];
                }
              }
            }
          }
        }
      }
    },

    processBlockedPackages(blockedPackages: Record<string, any>[]): void {
      if (!blockedPackages || !Array.isArray(blockedPackages)) return;

      this.summaryData.blocked_packages = blockedPackages.length;

      blockedPackages.forEach(pkg => {
        const userEmail = pkg.user_email || pkg.context?.runtime?.user?.name || pkg.user?.email;
        if (!userEmail) return;

        const user = this.users.find(u => u.email === userEmail);
        if (!user) return;

        // Use normalized package data from the backend if available
        if (pkg.package) {
          const blockedPackage = {
            name: pkg.package.name || 'Unknown',
            version: pkg.package.version || 'Unknown',
            project: pkg.package.project || pkg.context?.project?.id || 'Unknown',
            package_manager: pkg.package.package_manager || 'Unknown',
            install_path: pkg.package.install_path || 'Unknown',
            installed_at: pkg.installed_at,
            blocked: true
          };

          const existingIndex = user.packages.findIndex(p =>
            p.name === blockedPackage.name &&
            p.version === blockedPackage.version &&
            p.project === blockedPackage.project
          );

          if (existingIndex === -1) {
            user.packages.push(blockedPackage);
            user.total_packages++;
          } else {
            user.packages[existingIndex] = blockedPackage;
          }
          return;
        }

        // Fallback to legacy extraction logic
        // Extract package info from raw command if available
        let packageNameFromCommand = '';
        let packageVersionFromCommand = '';

        // Try to extract package from raw command
        if (pkg.raw_command && Array.isArray(pkg.raw_command) && pkg.raw_command.length > 0) {
          // Extract package info from command like "safety install package==version"
          const commandStr = pkg.raw_command.join(' ');
          const packagePattern = /\s([\w\-_.]+)==([\w\-_.]+)/g;
          const match = packagePattern.exec(commandStr);

          if (match && match.length >= 3) {
            packageNameFromCommand = match[1];
            packageVersionFromCommand = match[2];
          } else {
            // Try to find just the package name
            const packageArgIndex = pkg.raw_command.findIndex((arg: string, i: number) =>
              i > 0 &&
              arg &&
              !arg.startsWith('-') &&
              (arg.includes('.') || arg.match(/^[\w\-_]+$/))
            );

            if (packageArgIndex > 0) {
              const pkgArg = pkg.raw_command[packageArgIndex];
              if (pkgArg.includes('==')) {
                const parts = pkgArg.split('==');
                packageNameFromCommand = parts[0];
                packageVersionFromCommand = parts[1];
              } else {
                packageNameFromCommand = pkgArg;
              }
            }
          }
        }

        const blockedPackage = {
          name: pkg.package_name || pkg.package?.name || pkg.name || pkg.dependency?.name || pkg.dependency?.package_name || packageNameFromCommand || 'Unknown',
          version: pkg.version || pkg.package?.version || pkg.package?.spec || pkg.dependency?.version || pkg.dependency?.spec || packageVersionFromCommand || pkg.raw_command?.[2]?.split('==')?.[1] || 'Unknown',
          project: pkg.project || pkg.context?.project?.id || pkg.context?.project?.name || 'Unknown',
          package_manager: pkg.ecosystem || pkg.package_manager || pkg.tool || pkg.package?.package_manager || pkg.dependency?.package_manager || pkg.manager || pkg.context?.runtime?.package_manager || 'Unknown',
          install_path: pkg.install_path || 'Unknown',
          installed_at: pkg.installed_at,
          blocked: true
        };

        const existingIndex = user.packages.findIndex(p =>
          p.name === blockedPackage.name &&
          p.version === blockedPackage.version &&
          p.project === blockedPackage.project
        );

        if (existingIndex === -1) {
          user.packages.push(blockedPackage);
          user.total_packages++;
        } else {
          user.packages[existingIndex] = blockedPackage;
        }
      });

      // Sort packages by installation date after processing all blocked packages
      this.users.forEach(user => {
        user.packages.sort((a, b) => new Date(b.installed_at).getTime() - new Date(a.installed_at).getTime());
      });
    },

    processInstalledPackages(installedPackages: Record<string, any>[]): void {
      if (!installedPackages || !Array.isArray(installedPackages)) return;

      this.summaryData.analyzed_packages = installedPackages.length;

      installedPackages.forEach(pkg => {
        const userEmail = pkg.user_email || pkg.context?.runtime?.user?.name;
        if (!userEmail) return;

        const user = this.users.find(u => u.email === userEmail);
        if (!user) return;

        // Use normalized package data from the backend if available
        if (pkg.package) {
          const installedPackage = {
            name: pkg.package.name || 'Unknown',
            version: pkg.package.version || 'Unknown',
            project: pkg.package.project || pkg.context?.project?.id || 'Unknown',
            package_manager: pkg.package.package_manager || 'Unknown',
            install_path: pkg.install_path || pkg.package.install_path || 'Unknown',
            installed_at: pkg.installed_at || pkg.event_time || 'Never'
          };

          const existingIndex = user.packages.findIndex(p =>
            p.name === installedPackage.name &&
            p.version === installedPackage.version &&
            p.project === installedPackage.project
          );

          if (existingIndex === -1) {
            user.packages.push(installedPackage);
            user.total_packages++;
          } else {
            user.packages[existingIndex] = installedPackage;
          }
          return;
        }

        // Fallback to legacy extraction logic
        const installedPackage = {
          name: pkg.package_name || pkg.name,
          version: pkg.version || 'Unknown',
          project: pkg.context?.project?.id || pkg.project || 'Unknown',
          package_manager: pkg.ecosystem || pkg.package_manager || 'Unknown',
          install_path: pkg.install_path || 'Unknown',
          installed_at: pkg.installed_at || pkg.event_time || 'Never'
        };

        const existingIndex = user.packages.findIndex(p =>
          p.name === installedPackage.name &&
          p.version === installedPackage.version &&
          p.project === installedPackage.project
        );

        if (existingIndex === -1) {
          user.packages.push(installedPackage);
          user.total_packages++;
        } else {
          user.packages[existingIndex] = installedPackage;
        }
      });

      // Sort packages by installation date after processing all installed packages
      this.users.forEach(user => {
        user.packages.sort((a, b) => new Date(b.installed_at).getTime() - new Date(a.installed_at).getTime());
      });
    },

    renderPackagesTable(user: FirewallUser): string {
      const truncatePath = (path: string) => {
        const maxPathLength = 30;
        if (path.length > maxPathLength) {
          const parts = path.split('/');
          const fileName = parts.pop();
          if (!fileName) return path;
          const remainingPath = parts.join('/');
          return `${remainingPath.slice(0, maxPathLength - fileName.length - 3)}.../${fileName}`;
        }
        return path;
      };

      const truncateProject = (project: string) => {
        const maxLength = 20;
        if (project.length > maxLength) {
          return `${project.slice(0, maxLength)}...`;
        }
        return project;
      };

      const packages = user.packages || [];
      const sortedPackages = [...packages].sort((a, b) => new Date(b.installed_at).getTime() - new Date(a.installed_at).getTime());

      // Handle the case where there are no packages
      if (sortedPackages.length === 0) {
        return `
          <tr>
            <td colspan="6" class="px-4 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No packages installed using your protected package managers.
            </td>
          </tr>
        `;
      }

      const packagesToShow = user.showAllPackages ? sortedPackages : sortedPackages.slice(0, 5);

      let packageRows = packagesToShow.map(pkg => `
        <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <td class="px-4 py-2 text-sm text-gray-900 dark:text-white truncate">${this.escapeHtml(pkg.name)}</td>
          <td class="px-4 py-2 text-sm font-mono text-gray-700 dark:text-gray-300 truncate">${this.escapeHtml(pkg.version)}</td>
          <td class="px-4 py-2 text-sm text-gray-900 dark:text-white truncate" title="${this.escapeHtml(pkg.project || 'N/A')}">${this.escapeHtml(truncateProject(pkg.project || 'N/A'))}</td>
          <td class="px-4 py-2 text-sm text-gray-900 dark:text-white truncate">${this.escapeHtml(pkg.package_manager)}</td>
          <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 truncate" title="${this.escapeHtml(pkg.install_path)}">${this.escapeHtml(truncatePath(pkg.install_path))}</td>
          <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 truncate">${this.getRelativeTime(pkg.installed_at)}</td>
        </tr>
      `).join('');

      // Add "Load more" row if there are more packages and not showing all
      const loadMoreRow = !user.showAllPackages && sortedPackages.length > 5 ? `
        <tr class="see-more-row">
          <td colspan="6" class="px-4 py-2 text-center">
            <button @click="loadMorePackages('${user.id}')" class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 rounded-md px-3 py-1">
              Load ${sortedPackages.length - 5} more packages
            </button>
          </td>
        </tr>
      ` : '';

      return packageRows + loadMoreRow;
    },

    resetHighlights(preserveForPolling = false): void {
      // If preserving during polling and we have a search term, keep highlights completely intact
      if (preserveForPolling && this.currentSearchTerm) {
        return;
      }

      // Don't reset elements that are marked as preserved during polling
      document.querySelectorAll('.search-highlight:not(.preserved-highlight)').forEach(highlighted => {
        const parent = highlighted.parentElement;
        if (parent && parent.hasAttribute('data-original')) {
          parent.textContent = parent.getAttribute('data-original') || '';
          parent.removeAttribute('data-original');
        }
      });

      document.querySelectorAll('.no-matches-row').forEach(row => {
        row.remove();
      });

      document.querySelectorAll('tr').forEach(row => {
        if (!row.classList.contains('see-more-row')) {
          (row as HTMLElement).style.display = '';
        }

        // Only remove highlighted-row marker if not preserving
        if (!preserveForPolling) {
          row.classList.remove('highlighted-row');
        }
      });

      document.querySelectorAll('.flash-highlight').forEach(row => {
        row.classList.remove('flash-highlight');
      });
    },

    notifyNewEvent(): void {
      // No animations needed
    },

    async loadUserPackages(userEmail: string): Promise<void> {
      try {
        const userIndex = this.users.findIndex(u => u.email === userEmail);
        if (userIndex === -1) {
          console.error(`User with email ${userEmail} not found`);
          return;
        }

        const user = this.users[userIndex];
        const userId = user.id;
        user.isLoadingPackages = true;

        const response = await fetch(`api/user-packages/?email=${encodeURIComponent(userEmail)}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json() as {
          packages: FirewallPackage[];
          total_count: number;
        };

        // Use the standardized data from the serializer without fallbacks
        const updatedUser = {
          ...user,
          packages: data.packages || [],
          total_packages: data.total_count || 0,
          hasMorePackages: data.total_count > (data.packages?.length || 0),
          isLoadingPackages: false
        };

        this.users[userIndex] = updatedUser;

        const tableBody = document.getElementById(`${userId}-packages-tbody`);
        if (tableBody) {
          tableBody.innerHTML = this.renderPackagesTable(updatedUser);

          tableBody.classList.add('animate-fade-in');
          setTimeout(() => {
            tableBody.classList.remove('animate-fade-in');
          }, 500);
        }

      } catch (error: any) {
        console.error(`Error loading packages for user ${userEmail}:`, error);

        const userIndex = this.users.findIndex(u => u.email === userEmail);
        if (userIndex === -1) return;

        const user = this.users[userIndex];
        const userId = user.id;

        this.users[userIndex] = {
          ...user,
          isLoadingPackages: false
        };

        const tableBody = document.getElementById(`${userId}-packages-tbody`);
        if (tableBody) {
          tableBody.innerHTML = `
            <tr>
              <td colspan="6" class="px-4 py-3 text-center text-red-600">
                <div class="flex flex-col items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Failed to load packages: ${this.escapeHtml(error.message || 'Unknown error')}</span>
                  <button class="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                    onclick="document.getElementById('retry-load-${userId}').click()">
                    Retry
                  </button>
                  <button id="retry-load-${userId}" class="hidden"
                    onclick="Alpine.evaluate(document.getElementById('firewall-component'), 'loadUserPackages(\'${this.escapeHtml(userEmail)}\')')">
                    Retry Hidden
                  </button>
                </div>
              </td>
            </tr>
          `;
        }
      }
    },

    async loadMorePackages(userId: string): Promise<void> {
      const userIndex = this.users.findIndex(u => u.id === userId);
      if (userIndex === -1) return;

      const user = this.users[userIndex];
      user.showAllPackages = true;

      // Update the table with all packages
      const tableBody = document.getElementById(`${userId}-packages-tbody`);
      if (tableBody) {
        tableBody.innerHTML = this.renderPackagesTable(user);
      }
    },

    toggleEventDetails(eventIndex: number): void {
      if (eventIndex < 0 || eventIndex >= this.updates.length) return;

      const event = this.updates[eventIndex];
      if (!event) return;

      if (this.expandedEvents.has(eventIndex)) {
        this.expandedEvents.delete(eventIndex);
        event.expanded = false;
      } else {
        this.expandedEvents.add(eventIndex);
        event.expanded = true;

        if (event.expandable &&
            (!event.packages || event.packages.length === 0) &&
            event.raw_event?.data?.packages) {
          event.packages = event.raw_event.data.packages;
        }
      }

      this.$nextTick(() => {
        // Adjust the height of the events container after expanding/collapsing
        const updatesContainer = document.querySelector('#updates-container');
        if (updatesContainer) {
          const event = new CustomEvent('content-updated');
          updatesContainer.dispatchEvent(event);
        }
      });
    },

    $destroy(): void {
      // Cleanup code when the component is destroyed
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval);
      }
      if (this.requestTimeoutId) {
        clearTimeout(this.requestTimeoutId);
        this.requestTimeoutId = null;
      }
      // Remove the specific event listener using the stored bound handler
      if (this.boundRefreshHandler) {
        window.removeEventListener('firewall:refresh-events', this.boundRefreshHandler);
      }
      // Clean up inactivity timer and listeners
      if (this.inactivityTimeoutId) {
        clearTimeout(this.inactivityTimeoutId as number);
        this.inactivityTimeoutId = null;
      }
      // Use the same bound function reference for removal
      const boundResetTimer = this._resetInactivityTimer.bind(this);
      this.inactivityEvents.forEach(event => {
        window.removeEventListener(event, boundResetTimer);
      });
    },

    escapeHtml(unsafe: string): string {
      return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    obfuscateApiKey(command: string): string {
      // First escape the command
      const escapedCommand = this.escapeHtml(command);
      // Then replace API keys with the span for blurred text
      return escapedCommand
        .replace(/--key[= ]([^ ]+)/g, '--key=<span class="blurred-text">********</span>')
        .replace(/--api-key[= ]([^ ]+)/g, '--api-key=<span class="blurred-text">********</span>')
        .replace(/--apikey[= ]([^ ]+)/g, '--apikey=<span class="blurred-text">********</span>');
    },

    formatRawCommand(rawCommand: string[]): string {
      if (!Array.isArray(rawCommand) || rawCommand.length === 0) return '';

      // Join the command parts and clean up the path
      const command = rawCommand.join(' ')
        .replace(/^.*\/safety\s+/, 'safety ')  // Remove full path to safety
        .replace(/^.*\/__main__\.py\s+/, 'safety ')  // Handle __main__.py case
        .replace(/^.*\/python\d+\.\d+\/site-packages\/safety\s+/, 'safety ')  // Handle site-packages path
        .replace(/^.*\\safety\s+/, 'safety ')  // Handle Windows paths
        .replace(/^.*\\pyenv-win\\versions\\\d+\.\d+\.\d+\\Scripts\\safety\s+/, 'safety ')  // Handle pyenv-win paths
        .replace(/^.*\\Program Files\\Python\\Scripts\\safety\.exe\s+/, 'safety ')  // Handle Windows Program Files path
        .replace(/^.*\\Python\\Scripts\\safety\.exe\s+/, 'safety ');  // Handle other Windows Python paths

      // Obfuscate any API keys
      return this.obfuscateApiKey(command);
    },

    // New methods for skeleton handling
    ensureSkeletonsVisible(): void {
      // Make sure all skeletons are visible during loading
      const statusSkeleton = document.querySelector('.status-skeleton');
      const protectedSkeleton = document.querySelector('.protected-skeleton');
      const metricsSkeleton = document.querySelector('.metrics-skeleton');
      const policySkeleton = document.getElementById('policy-skeleton');

      // Helper function to ensure an element is visible - using both style and important CSS
      const makeVisible = (el: Element | null) => {
        if (el) {
          (el as HTMLElement).style.display = 'block';
          (el as HTMLElement).style.opacity = '1';
          (el as HTMLElement).style.visibility = 'visible';

          // Also force through !important inline styles
          (el as HTMLElement).setAttribute('style',
            'display: block !important; opacity: 1 !important; visibility: visible !important;'
          );
        }
      };

      // Ensure all skeletons are visible
      makeVisible(statusSkeleton);
      makeVisible(protectedSkeleton);
      makeVisible(metricsSkeleton);
      makeVisible(policySkeleton);

      // Also force hide content elements initially
      const contentElements = document.querySelectorAll('[x-show="!isLoadingUsers"]');
      contentElements.forEach(el => {
        (el as HTMLElement).style.display = 'none';
        // Force through !important
        (el as HTMLElement).setAttribute('style', 'display: none !important;');
      });

      // Make sure isLoadingUsers state is true so Alpine renders correctly
      this.isLoadingUsers = true;
    },

    checkAndShowContent(): void {
      // This function should ONLY manage metrics/policy visibility based on isLoadingMetrics
      // It should NOT affect summary skeletons (status, protected)
      // It should NOT add the metrics-loaded class
      if (!this.isLoadingMetrics) {
        const metricsSkeleton = document.querySelector('.metrics-skeleton');
        const policySkeleton = document.getElementById('policy-skeleton');
        const metricsContent = document.getElementById('metrics-content');
        const policyContent = document.getElementById('policy-content');

        const hideElement = (el: Element | null) => {
          if (el) {
            // Immediately and forcefully hide the skeleton
            (el as HTMLElement).setAttribute('style',
              'display: none !important; opacity: 0 !important; visibility: hidden !important;'
            );
          }
        };

        const showElement = (el: Element | null) => {
          if (el) {
            // Remove any !important display styles and set to block for transition
            (el as HTMLElement).setAttribute('style', 'display: block; opacity: 0; visibility: hidden;');
            // Force a reflow before starting the transition
            void (el as HTMLElement).offsetHeight;
            // Start fade-in
            (el as HTMLElement).style.opacity = '1';
            (el as HTMLElement).style.visibility = 'visible';
          }
        };

        // Hide metrics and policy skeletons ONLY
        hideElement(metricsSkeleton);
        hideElement(policySkeleton);

        // Show metrics and policy content ONLY
        showElement(metricsContent);
        showElement(policyContent);
      }
      // DO NOT touch status-skeleton or protected-skeleton here
      // DO NOT add metrics-loaded class to body here
    },

    setupCardHeightMatching(): void {
      const summaryCard = document.querySelector('.firewall-card');
      const eventsCard = document.querySelector('.firewall-events-card');

      const matchHeights = () => {
        const summaryHeader = summaryCard ? summaryCard.querySelector('.px-6.py-4') : null;
        const eventsHeader = eventsCard ? eventsCard.querySelector('.px-6.py-4') : null;

        if (summaryCard && eventsCard && summaryHeader && eventsHeader) {
          // Get the summary card's full height including its skeleton loaders
          const summaryHeight = summaryCard.getBoundingClientRect().height;

          // Reset any previous padding adjustments
          (summaryHeader as HTMLElement).style.paddingBottom = '';
          (eventsHeader as HTMLElement).style.paddingBottom = '';

          // Calculate the total height of content including padding and margins
          const getTotalHeight = (element: Element) => {
            const style = getComputedStyle(element);
            return element.getBoundingClientRect().height +
                  parseFloat(style.marginTop) +
                  parseFloat(style.marginBottom) +
                  parseFloat(style.paddingTop) +
                  parseFloat(style.paddingBottom);
          };

          const summaryTotalHeight = getTotalHeight(summaryHeader);
          const eventsTotalHeight = getTotalHeight(eventsHeader);

          // Ensure header heights match for proper horizontal rule alignment
          if (summaryTotalHeight !== eventsTotalHeight) {
            const headerDiff = Math.abs(summaryTotalHeight - eventsTotalHeight);
            if (summaryTotalHeight > eventsTotalHeight) {
              const currentPadding = parseFloat(getComputedStyle(eventsHeader).paddingBottom);
              (eventsHeader as HTMLElement).style.paddingBottom = (currentPadding + headerDiff) + 'px';
            } else {
              const currentPadding = parseFloat(getComputedStyle(summaryHeader).paddingBottom);
              (summaryHeader as HTMLElement).style.paddingBottom = (currentPadding + headerDiff) + 'px';
            }
          }

          // Add a small delay to ensure smooth transition
          requestAnimationFrame(() => {
            // Set the height of the events card to match the summary card
            (eventsCard as HTMLElement).style.height = summaryHeight + 'px';
            (eventsCard as HTMLElement).style.minHeight = summaryHeight + 'px';

            // Update the events scroll container
            const eventsScroll = document.querySelector('.firewall-events-scroll');
            if (eventsScroll) {
              const headerHeight = eventsHeader.getBoundingClientRect().height;
              const newMaxHeight = summaryHeight - headerHeight;

              // Only update if the height has changed significantly
              const currentMaxHeight = parseInt((eventsScroll as HTMLElement).style.maxHeight) || 0;
              if (Math.abs(currentMaxHeight - newMaxHeight) > 1) {
                (eventsScroll as HTMLElement).style.maxHeight = newMaxHeight + 'px';
                (eventsScroll as HTMLElement).style.minHeight = Math.min(180, newMaxHeight) + 'px';
              }
            }
          });
        }
      };

      // Run on load and any time the DOM content might change
      matchHeights();

      // Also run after a short delay to account for any rendering delays
      setTimeout(matchHeights, 50);
      setTimeout(matchHeights, 300);

      // Call again after Alpine.js initializes the data
      document.addEventListener('alpine:initialized', matchHeights);

      // Debounce resize events to prevent too many updates
      let resizeTimeout: number | null = null;
      window.addEventListener('resize', () => {
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }
        resizeTimeout = window.setTimeout(matchHeights, 100) as unknown as number;
      });

      // Watch for changes in the updates container
      const observer = new MutationObserver(() => {
        matchHeights();
      });

      // Find the updates container for the observer
      const updatesContainer = document.querySelector('#updates-container');
      if (updatesContainer) {
        observer.observe(updatesContainer, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      // Observe the summary card for changes as well
      if (summaryCard) {
        observer.observe(summaryCard, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        });
      }
    },

    // Define the handler method
    handleRefreshEvent(event: Event): void {
      const customEvent = event as CustomEvent;
      // Store the event ID to avoid duplicate processing
      if (customEvent.detail.eventId) {
        this.processedEventIds.add(customEvent.detail.eventId);
      }

      // Check if there's an active search before refreshing
      const hasActiveSearch = this.currentSearchTerm && this.currentSearchTerm.trim() !== '';

      // Set pending requests flag
      this.hasPendingRequests = true;

      // Watchdog timer for this request cycle
      if (this.requestTimeoutId) {
        clearTimeout(this.requestTimeoutId);
      }
      this.requestTimeoutId = window.setTimeout(() => {
        if (this.hasPendingRequests) {
          console.warn('Firewall refresh request cycle timed out. Forcing hasPendingRequests to false.');
          this.hasPendingRequests = false;
        }
        this.requestTimeoutId = null;
      }, 30000); // 30-second watchdog

      const promises = [];

      // Only refresh firewall status data and package metrics if not in search mode
      if (!hasActiveSearch) {
        // Refresh firewall status data
        promises.push(
          this.fetchFirewallData()
            .catch(error => {
              console.error('Error refreshing firewall data:', error);
              // Add retry logic - attempt again after a delay
              setTimeout(() => {
                this.fetchFirewallData().catch(retryError => {
                  console.error('Error in retry of firewall data:', retryError);
                });
              }, 3000);
            })
        );

        // Refresh package metrics
        promises.push(
          this.fetchPackageMetrics()
            .catch(error => {
              console.error('Error refreshing package metrics:', error);
            })
        );
      }

      // Always refresh events data from API, even during search
      promises.push(
        this.fetchTelemetryEvents(false, true)
          .then(() => {
            this.notifyNewEvent();
          })
          .catch(error => {
            console.error('Error refreshing events:', error);
            // Don't try to retry this immediately to avoid overwhelming the server
          })
      );

      Promise.allSettled(promises).finally(() => {
        // Clear the watchdog timer
        if (this.requestTimeoutId) {
          clearTimeout(this.requestTimeoutId);
          this.requestTimeoutId = null;
        }
        // Clear pending requests flag when all requests are complete
        this.hasPendingRequests = false;
      });
    },

    // Method to check if initial load filled the view, and load more if not
    checkInitialScrollAndLoadMore() {
      this.$nextTick(() => {
        const eventsContainer = document.querySelector('.firewall-events-scroll');
        if (eventsContainer && this.hasMoreEvents && !this.isLoadingMore) {
          if (eventsContainer.scrollHeight <= eventsContainer.clientHeight) {
            console.log('Firewall: Initial event load did not fill the view. Attempting to load more events automatically.');
            this.loadMoreEvents(false);
          }
        }
      });
    },

    _resetInactivityTimer() {
      if (this.inactivityTimeoutId) {
        clearTimeout(this.inactivityTimeoutId as number);
      }
      // Set timeout for 1 hour (3600000 milliseconds)
      this.inactivityTimeoutId = setTimeout(() => {
        // Use window.location.reload() for a full page refresh
        window.location.reload();
      }, 3600000);
    }
  };
}

export const firewallIndicator = () => ({
    isEnabled: false,
    isProtected: false,
    isLoading: true,

    async init() {
        try {
            const response = await fetch('/firewall/api/user-status/');
            if (!response.ok) {
                throw new Error('Failed to fetch firewall status');
            }
            const data = await response.json();
            this.isEnabled = data.is_enabled;
            this.isProtected = data.is_protected;
        } catch (error) {
            console.error('Error fetching firewall status:', error);
            this.isEnabled = false;
            this.isProtected = false;
        } finally {
            this.isLoading = false;
        }
    }
});
